import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { writePref } from '../shared/clientPrefs';
import { readCachedI18n, writeCachedI18n } from './cache';
import { detectInitialLanguage, getDirection, normalizeLanguageTag, type Direction, type SupportedLanguage } from './languages';
import { localI18nOverridesFor } from './localOverrides';
import { ONBOARDING_FALLBACK } from './onboardingFallback';
import { translateValue, type Replacements, type TranslationMap } from './translate';
import { API_CREDENTIALS } from '../api/http-credentials';

interface I18nApiResponse {
  _lang?: string;
  _fallbackChain?: string[];
  keys?: TranslationMap;
  _emojis?: Record<string, unknown>;
}

export interface I18nContextValue {
  lang: SupportedLanguage;
  dir: Direction;
  t: (key: string, replacements?: Replacements) => string;
  setLang: (lang: string) => void;
  /**
   * True when cached/server translations are available, or after a short wait
   * when the bundled DE/EN first-launch vocabulary takes over offline.
   */
  ready: boolean;
  diagnostics: I18nDiagnostics;
}

export interface I18nDiagnostics {
  lastError: { url: string; message: string } | null;
  mapSizes: { active: number; en: number };
  lang: SupportedLanguage;
  fetchCount: number;
  lastSuccessAt: string | null;
}

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
const I18nContext = createContext<I18nContextValue | null>(null);
const I18N_RETRY_DELAYS_MS = [0, 500, 1500] as const;
const I18N_EMPTY_REFETCH_MS = 15000;

interface I18nFetchFailure {
  url: string;
  error: unknown;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function i18nUrl(lang: SupportedLanguage): string {
  return `${API_BASE}/api/i18n/${encodeURIComponent(lang)}`;
}

function isI18nFetchFailure(error: unknown): error is I18nFetchFailure {
  return Boolean(error && typeof error === 'object' && 'url' in error && 'error' in error);
}

function errorDetail(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function mapIsEmpty(map: TranslationMap): boolean {
  return Object.keys(map).length === 0;
}

function mapsAreEmpty(maps: { active: TranslationMap; en: TranslationMap }): boolean {
  return mapIsEmpty(maps.active) && mapIsEmpty(maps.en);
}

async function fetchI18nMap(lang: SupportedLanguage): Promise<TranslationMap> {
  const response = await fetch(i18nUrl(lang), {
    credentials: API_CREDENTIALS,
    // WebView-HTTP-Cache servierte stale Maps (neue Keys blieben rohe Strings,
    // bis pm clear den Cache loeschte — Emulator-Befund 2026-06-10). Immer
    // gegen den Server revalidieren; der eigene localStorage-Cache bleibt
    // der Offline-Fallback.
    cache: 'no-cache',
  });
  if (!response.ok) throw new Error(`i18n ${lang} failed`);
  const data = await response.json() as I18nApiResponse;
  return data.keys ?? {};
}

async function fetchI18nMapWithRetry(lang: SupportedLanguage): Promise<TranslationMap> {
  const url = i18nUrl(lang);
  let lastError: unknown = null;

  for (const delay of I18N_RETRY_DELAYS_MS) {
    if (delay > 0) await wait(delay);
    try {
      return await fetchI18nMap(lang);
    } catch (error) {
      lastError = error;
    }
  }

  throw { url, error: lastError } satisfies I18nFetchFailure;
}

function readInitialMaps(lang: SupportedLanguage): { active: TranslationMap; en: TranslationMap } {
  return readCachedI18n(lang) ?? { active: {}, en: {} };
}

function applyHtmlLanguage(lang: SupportedLanguage, dir: Direction): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
  document.documentElement.dir = dir;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [offlineReady, setOfflineReady] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setOfflineReady(true), 2500);
    return () => window.clearTimeout(timer);
  }, []);
  const [lang, setLangState] = useState<SupportedLanguage>(() => detectInitialLanguage());
  const [maps, setMaps] = useState(() => readInitialMaps(detectInitialLanguage()));
  const [diagnosticsState, setDiagnosticsState] = useState<Pick<I18nDiagnostics, 'lastError' | 'fetchCount' | 'lastSuccessAt'>>({
    lastError: null,
    fetchCount: 0,
    lastSuccessAt: null,
  });
  const mapsRef = useRef(maps);
  const dir = getDirection(lang);
  const activeTranslations = useMemo(
    () => ({ ...ONBOARDING_FALLBACK[lang === 'de' ? 'de' : 'en'], ...maps.active, ...localI18nOverridesFor(lang) }),
    [lang, maps.active],
  );
  const enTranslations = useMemo(
    () => ({ ...ONBOARDING_FALLBACK.en, ...maps.en, ...localI18nOverridesFor('en') }),
    [maps.en],
  );

  useEffect(() => {
    mapsRef.current = maps;
  }, [maps]);

  useEffect(() => {
    applyHtmlLanguage(lang, dir);
  }, [dir, lang]);

  useEffect(() => {
    let cancelled = false;
    const cached = readCachedI18n(lang);
    if (cached) setMaps(cached);

    async function refreshMaps() {
      setDiagnosticsState((current) => ({ ...current, fetchCount: current.fetchCount + 1 }));
      const cacheBeforeFetch = readCachedI18n(lang);
      // J-02: bei lang === 'en' sind aktive Sprache und EN-Fallback dieselbe
      // URL — nur EINMAL laden statt zwei identische parallele GET-Requests.
      const [activeResult, enResult] = lang === 'en'
        ? await Promise.allSettled([fetchI18nMapWithRetry('en')]).then(([result]) => [result, result])
        : await Promise.allSettled([fetchI18nMapWithRetry(lang), fetchI18nMapWithRetry('en')]);
      if (cancelled) return;

      const active = activeResult.status === 'fulfilled' ? activeResult.value : cacheBeforeFetch?.active ?? {};
      const en = enResult.status === 'fulfilled' ? enResult.value : cacheBeforeFetch?.en ?? {};
      const next = { active, en };

      if (activeResult.status === 'fulfilled' || enResult.status === 'fulfilled') {
        setMaps(next);
        writeCachedI18n(lang, active, en);
      }

      const failedFetches = [activeResult, enResult]
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map((result) => {
          if (isI18nFetchFailure(result.reason)) {
            return {
              url: result.reason.url,
              message: errorDetail(result.reason.error),
            };
          }
          return {
            url: i18nUrl(lang),
            message: errorDetail(result.reason),
          };
        });
      const lastFailedFetch = failedFetches.length > 0 ? failedFetches[failedFetches.length - 1] : null;

      setDiagnosticsState((current) => ({
        ...current,
        lastError: lastFailedFetch,
        lastSuccessAt: activeResult.status === 'fulfilled' || enResult.status === 'fulfilled'
          ? new Date().toISOString()
          : current.lastSuccessAt,
      }));

      if (!cacheBeforeFetch) {
        [activeResult, enResult].forEach((result) => {
          if (result.status === 'rejected' && isI18nFetchFailure(result.reason)) {
            console.error('[i18n] translation fetch failed', {
              url: result.reason.url,
              error: errorDetail(result.reason.error),
            });
          }
        });
      }
    }

    void refreshMaps();
    const emptyMapTimer = window.setInterval(() => {
      if (mapsAreEmpty(mapsRef.current)) void refreshMaps();
    }, I18N_EMPTY_REFETCH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(emptyMapTimer);
    };
  }, [lang]);

  const setLang = useCallback((nextLang: string) => {
    const normalized = normalizeLanguageTag(nextLang) ?? 'en';
    // Cookie + localStorage (Write-Through gegen den iOS-Cookie-Verlust, clientPrefs.ts).
    writePref('ff_lang', normalized);
    setLangState(normalized);
    const cached = readCachedI18n(normalized);
    setMaps(cached ?? { active: {}, en: {} });
  }, []);

  const t = useCallback((key: string, replacements?: Replacements) => {
    return translateValue(key, activeTranslations, enTranslations, replacements, lang);
  }, [activeTranslations, enTranslations, lang]);

  const diagnostics = useMemo<I18nDiagnostics>(() => ({
    ...diagnosticsState,
    lang,
    mapSizes: {
      active: Object.keys(maps.active).length,
      en: Object.keys(maps.en).length,
    },
  }), [diagnosticsState, lang, maps.active, maps.en]);

  const ready = maps.active && Object.keys(maps.active).length > 0
    ? true
    : Object.keys(maps.en).length > 0 || offlineReady;

  const value = useMemo<I18nContextValue>(() => ({
    lang,
    dir,
    t,
    setLang,
    ready,
    diagnostics,
  }), [diagnostics, dir, lang, ready, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useT must be used within I18nProvider');
  return value;
}

/**
 * Nicht-werfende Variante von useT: liefert den I18n-Kontext oder null, wenn
 * kein I18nProvider vorhanden ist. Fuer Bauteile, die auch in isolierten Tests
 * (ohne Provider) mounten koennen und dort auf einen literalen Fallback
 * ausweichen — z.B. das best-effort aria-live-Statusfeld der SplitView.
 */
export function useOptionalT(): I18nContextValue | null {
  return useContext(I18nContext);
}
